import { clientLogger } from '@/lib/clientLogger';
import { useEffect, useRef, useState, useCallback } from 'react';
import { ENV } from '@/config/environment';
import { loadGoogleMaps, isGoogleMapsReady } from '@/lib/loadGoogleMaps';
import { setCachedGeocode } from '@/lib/geocodeCache';

interface UseGoogleMapsAutocompleteProps {
  inputRef: React.RefObject<HTMLInputElement>;
  onPlaceSelect?: (place: google.maps.places.PlaceResult) => void;
  options?: google.maps.places.AutocompleteOptions;
  isActive?: boolean;
}

/**
 * Extracts the zip code (postal code) from Google Maps place address_components
 */
export function extractZipCode(place: google.maps.places.PlaceResult): string | null {
  if (!place.address_components) return null;

  const postalComponent = place.address_components.find(
    (component) => component.types.includes('postal_code')
  );

  return postalComponent?.short_name || postalComponent?.long_name || null;
}

/**
 * Gets the formatted address with zip code guaranteed to be included.
 * If the formatted_address doesn't already contain the zip code, it appends it.
 * Always removes the country suffix (", USA" or ", United States").
 */
export function getAddressWithZipCode(place: google.maps.places.PlaceResult): string {
  let formattedAddress = place.formatted_address || '';
  const zipCode = extractZipCode(place);
  
  // Remove trailing ", USA" or ", United States" (and translations in Hebrew, Spanish)
  const usaSuffixRegex = /,?\s*(USA|United States|Estados Unidos|ארצות הברית)\s*$/i;
  formattedAddress = formattedAddress.replace(usaSuffixRegex, '').trim();
  
  if (!zipCode) {
    return formattedAddress;
  }
  
  // Check if the zip code is already in the formatted address
  if (formattedAddress.includes(zipCode)) {
    return formattedAddress;
  }
  
  // Append zip code if not present
  return `${formattedAddress} ${zipCode}`;
}

export function useGoogleMapsAutocomplete({
  inputRef,
  onPlaceSelect,
  options = {},
  isActive = true,
}: UseGoogleMapsAutocompleteProps) {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autocompleteElementRef = useRef<google.maps.places.Autocomplete | null>(null);
  const isInitializedRef = useRef(false);
  const cleanupRef = useRef<(() => void) | null>(null);

  // Store onPlaceSelect in a ref so the listener always uses the latest version
  const onPlaceSelectRef = useRef(onPlaceSelect);
  onPlaceSelectRef.current = onPlaceSelect;

  const initializeAutocomplete = useCallback(() => {
    // Skip if already initialized - prevents constant recreation
    if (isInitializedRef.current && autocompleteElementRef.current) {
      return;
    }

    if (!inputRef.current || !isGoogleMapsReady()) {
      return;
    }

    try {
      // Clear any existing autocomplete
      if (autocompleteElementRef.current) {
        cleanupRef.current?.();
        autocompleteElementRef.current = null;
        isInitializedRef.current = false;
      }

      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        {
          types: ['address'],
          componentRestrictions: { country: 'us' },
          ...options
        }
      );

      // Only pull the fields we use — otherwise every selection is billed for the
      // full (most-expensive) Place Details response. geometry is free here and
      // gets cached so map views never re-geocode this address.
      autocomplete.setFields(['address_components', 'formatted_address', 'geometry']);

      autocompleteElementRef.current = autocomplete;
      isInitializedRef.current = true;

      // Listen for place selection
      const listener = autocomplete.addListener('place_changed', () => {
        try {
          const place = autocomplete.getPlace();
          if (place && onPlaceSelectRef.current) {
            const loc = place.geometry?.location;
            if (loc) {
              setCachedGeocode(getAddressWithZipCode(place), { lat: loc.lat(), lng: loc.lng() });
            }
            onPlaceSelectRef.current(place);
          }
        } catch (error) {
          clientLogger.error('Error in place_changed handler:', error);
        }
      });

      // Store cleanup function
      cleanupRef.current = () => {
        if (listener && window.google?.maps?.event) {
          window.google.maps.event.removeListener(listener);
        }
        if (autocompleteElementRef.current) {
          autocompleteElementRef.current = null;
        }
        isInitializedRef.current = false;
      };

    } catch (err) {
      clientLogger.error('Autocomplete initialization failed:', err);
      setError('Failed to initialize address autocomplete');
      // Ensure input remains enabled even if autocomplete initialization fails
      if (inputRef.current) {
        inputRef.current.disabled = false;
      }
    }
  }, [inputRef, onPlaceSelect, options]);

  useEffect(() => {
    if (!isActive) {
      return;
    }

    // Only try to initialize if we have an API key
    if (!ENV.GOOGLE_MAPS.API_KEY) {
      setIsLoaded(true);
      setError('Google Maps API key not configured');
      return;
    }

    // Wait for input element to exist (it might be conditionally rendered)
    let waitAttempts = 0;
    const maxWaitAttempts = 20; // 2 seconds max
    let waitIntervalId: NodeJS.Timeout | null = null;

    const tryInitialize = () => {
      if (!inputRef.current) {
        waitAttempts++;
        if (waitAttempts >= maxWaitAttempts) {
          if (waitIntervalId) clearInterval(waitIntervalId);
          clientLogger.warn('Input element not found for autocomplete');
          return;
        }
        return; // Keep waiting
      }

      // Input found, stop waiting
      if (waitIntervalId) clearInterval(waitIntervalId);

      if (isInitializedRef.current) {
        return;
      }

      // Set up global error handler for Google Maps authentication errors
      const gm_authFailure = () => {
        clientLogger.error('Google Maps authentication failed');
        setError('Google Maps authentication failed');
        if (inputRef.current) {
          inputRef.current.disabled = false;
        }
      };
      (window as any).gm_authFailure = gm_authFailure;

      // Load Google Maps API and initialize autocomplete
      loadGoogleMaps()
        .then(() => {
          setIsLoaded(true);
          if (isGoogleMapsReady()) {
            setError(null);
            // Add a small delay to ensure the API is fully ready
            setTimeout(initializeAutocomplete, 100);
          } else {
            setError('Google Maps API loaded but not ready');
          }
        })
        .catch((err) => {
          clientLogger.error('Failed to load Google Maps API:', err);
          setIsLoaded(true);
          setError('Failed to load Google Maps API');
          if (inputRef.current) {
            inputRef.current.disabled = false;
          }
        });
    };

    // Start polling for input element
    waitIntervalId = setInterval(tryInitialize, 100);
    tryInitialize(); // Also try immediately

    // Cleanup function
    return () => {
      if (waitIntervalId) clearInterval(waitIntervalId);
      cleanupRef.current?.();
      delete (window as any).gm_authFailure;
    };
  }, [isActive, inputRef]);

  return {
    isLoaded,
    error,
    autocomplete: autocompleteElementRef.current
  };
}